import { initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  type Auth,
  type User as FirebaseUser,
  type Unsubscribe,
} from 'firebase/auth';

export interface CitiboxFirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  /** Hosted Domain restriction for Google SSO (default: 'citibox.com'). */
  allowedDomain?: string;
}

let appSingleton: FirebaseApp | null = null;
let authSingleton: Auth | null = null;
let allowedDomain = 'citibox.com';

export function initFirebase(config: CitiboxFirebaseConfig): { app: FirebaseApp; auth: Auth } {
  const firebaseOptions: FirebaseOptions = {
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    appId: config.appId,
  };
  allowedDomain = config.allowedDomain ?? 'citibox.com';
  appSingleton = initializeApp(firebaseOptions);
  authSingleton = getAuth(appSingleton);
  return { app: appSingleton, auth: authSingleton };
}

function requireAuth(): Auth {
  if (authSingleton === null) {
    throw new Error('Firebase not initialized. Call initFirebase() at app bootstrap.');
  }
  return authSingleton;
}

// The domain rule the sign-in enforces, for code that sees a user Firebase reported before that
// check signed it out again.
export function isAllowedAccount(email: string | null): boolean {
  return email?.endsWith(`@${allowedDomain}`) ?? false;
}

/** True once initFirebase() has run with a valid config. */
export function isFirebaseInitialized(): boolean {
  return authSingleton !== null;
}

export class NonCitiboxEmailError extends Error {
  public override readonly name = 'NonCitiboxEmailError';
}

export async function signInWithGoogle(): Promise<FirebaseUser> {
  const auth = requireAuth();
  const provider = new GoogleAuthProvider();
  // hd restringe el dominio sugerido; select_account fuerza el selector para que
  // el usuario pueda elegir/cambiar de cuenta en lugar de reusar la última sesión.
  provider.setCustomParameters({ hd: allowedDomain, prompt: 'select_account' });

  const result = await signInWithPopup(auth, provider);
  if (!isAllowedAccount(result.user.email)) {
    await firebaseSignOut(auth);
    // Solo el dominio del email: identifica el intento sin loguear el email entero.
    const domain = result.user.email?.split('@')[1] ?? 'unknown';
    console.warn('[auth] domainRejected', { emailDomain: `@${domain}` });
    throw new NonCitiboxEmailError(
      `Email ${result.user.email ?? '(none)'} is not from @${allowedDomain}`,
    );
  }
  return result.user;
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(requireAuth());
}

export function getIdToken(forceRefresh = false): Promise<string> {
  const user = requireAuth().currentUser;
  if (!user) {
    return Promise.reject(new Error('No authenticated user.'));
  }
  return user.getIdToken(forceRefresh);
}

export function onAuthStateChanged(callback: (user: FirebaseUser | null) => void): Unsubscribe {
  // If Firebase wasn't initialised (config missing for the environment), emit
  // null synchronously so AuthProvider exits its loading state and the SPA
  // can render. Return a no-op unsubscribe.
  if (authSingleton === null) {
    callback(null);
    return () => {
      /* no-op */
    };
  }
  return firebaseOnAuthStateChanged(authSingleton, callback);
}

export type { FirebaseUser };
